import React, { useState, useEffect } from "react";
import CustomModal from "../../components/CustomModal";
import { useCreateDeal, useUpdateDeal } from "./hooks";
import { Deal } from "./api";
import { isValidPartitaIVA } from "../../utils/vat";
import { useT } from "../../i18n";

interface NewDealModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  editDeal?: Deal | null;
}

const regions = [
  "Emilia-Romagna",
  "Lazio",
  "Lombardia",
  "Piemonte",
  "Veneto",
  "Toscana",
  "Sicilia",
  "Liguria",
  "Umbria",
  "Abruzzo",
];

const services = [
  "Luxia Analytics",
  "Luxia Documents",
  "Luxia Avatar",
  "Luxia GPT",
  "Luxia Special Project",
];

const NewDealModal: React.FC<NewDealModalProps> = ({
  open,
  onClose,
  onSuccess,
  editDeal,
}) => {
  const t = useT();
  const [company, setCompany] = useState("");
  const [vat, setVat] = useState("");
  const [contact, setContact] = useState("");
  const [region, setRegion] = useState("");
  const [note, setNote] = useState("");
  const [selectedServices, setSelectedServices] = useState<string[]>([]);
  const [error, setError] = useState("");

  const isEditing = !!editDeal;

  // Use hooks for API calls
  const createDealMutation = useCreateDeal();
  const updateDealMutation = useUpdateDeal();

  // Populate form when editing
  useEffect(() => {
    if (editDeal) {
      setCompany(editDeal.companyName || "");
      setVat(editDeal.vatNumber || "");
      setContact(editDeal.contactPerson || "");
      setRegion(editDeal.region || "");
      setNote(editDeal.note || "");
      setSelectedServices(editDeal.services || []);
    } else {
      resetForm();
    }
  }, [editDeal, open]);

  const handleServiceToggle = (service: string) => {
    setSelectedServices((prev) =>
      prev.includes(service)
        ? prev.filter((s) => s !== service)
        : [...prev, service]
    );
  };

  const resetForm = () => {
    setCompany("");
    setVat("");
    setContact("");
    setRegion("");
    setNote("");
    setSelectedServices([]);
    setError("");
  };

  const handleSave = async () => {
    setError("");

    // Basic validation
    if (!company || !vat || !contact || !region) {
      setError(t("deals.modal.errors.requiredFields"));
      return;
    }

    // Formal VAT (Partita IVA) validation before anything else.
    // The backend still re-checks this and whether the VAT is already taken.
    if (!isValidPartitaIVA(vat)) {
      setError(t("deals.modal.errors.invalidVat"));
      return;
    }

    try {
      const dealData = {
        companyName: company,
        vatNumber: vat,
        contactPerson: contact,
        region,
        note,
        services: selectedServices,
      };

      if (isEditing && editDeal) {
        // Update existing deal
        await updateDealMutation.mutateAsync({
          uuid: editDeal.uuid,
          data: dealData,
        });
      } else {
        // Create new deal
        await createDealMutation.mutateAsync(dealData);
      }

      // Success - close modal and reset form
      resetForm();
      onClose();
      if (onSuccess) {
        onSuccess();
      }
    } catch (error: any) {
      setError(
        t(isEditing ? "deals.toast.updateFailed" : "deals.toast.createFailed")
      );
    }
  };

  return (
    <CustomModal open={open} onClose={onClose}>
      <div
        className="w-[820px] max-w-full h-full flex flex-col mx-auto"
        style={{ height: "100%" }}
      >
        <div className="flex flex-col w-full h-full" style={{ height: "100%" }}>
          <div className="flex items-center gap-3 pt-8 pb-2 border-b border-gray-200 w-full pl-0 pr-0">
            <div className="flex items-center justify-center w-10 h-10 rounded-lg bg-teal-50 ml-0">
              <i
                className={`fa-solid ${
                  isEditing ? "fa-edit" : "fa-plus"
                } text-2xl text-teal-500`}
              ></i>
            </div>
            <div className="ml-0">
              <h2 className="text-xl font-semibold text-gray-900">
                {isEditing ? t("deals.modal.editTitle") : t("deals.modal.createTitle")}
              </h2>
              <div className="text-sm text-gray-400 font-medium leading-tight">
                {isEditing
                  ? t("deals.modal.editSubtitle")
                  : t("deals.modal.createSubtitle")}
              </div>
            </div>
          </div>
          <form
            className="py-8 space-y-5 w-full flex flex-col justify-center px-0 flex-1"
            style={{ height: "100%" }}
            onSubmit={(e) => {
              e.preventDefault();
              handleSave();
            }}
          >
            <div className="px-0">
              <label className="block text-sm text-gray-700 mb-1 ml-0">
                {t("deals.modal.companyNameLabel")}
              </label>
              <input
                type="text"
                className="w-full border border-gray-300 rounded-lg px-4 py-2 text-gray-700 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-teal-200"
                value={company}
                onChange={(e) => setCompany(e.target.value)}
                placeholder={t("deals.modal.companyNamePlaceholder")}
                required
              />
            </div>
            <div className="px-0">
              <label className="block text-sm text-gray-700 mb-1 ml-0">
                {t("deals.modal.vatNumberLabel")}
              </label>
              <input
                type="text"
                className="w-full border border-gray-300 rounded-lg px-4 py-2 text-gray-700 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-teal-200"
                value={vat}
                onChange={(e) => setVat(e.target.value.toUpperCase())}
                placeholder={t("deals.modal.vatNumberPlaceholder")}
                required
              />
            </div>
            <div className="px-0">
              <label className="block text-sm text-gray-700 mb-1 ml-0">
                {t("deals.modal.contactPersonLabel")}
              </label>
              <input
                type="text"
                className="w-full border border-gray-300 rounded-lg px-4 py-2 text-gray-700 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-teal-200"
                value={contact}
                onChange={(e) => setContact(e.target.value)}
                placeholder={t("deals.modal.contactPersonPlaceholder")}
                required
              />
            </div>
            <div className="px-0">
              <label className="block text-sm text-gray-700 mb-1 ml-0">
                {t("deals.modal.regionLabel")}
              </label>
              <select
                className="w-full border border-gray-300 rounded-lg px-4 py-2 text-gray-700 focus:outline-none focus:ring-2 focus:ring-teal-200"
                value={region}
                onChange={(e) => setRegion(e.target.value)}
                required
              >
                <option value="">{t("deals.modal.regionPlaceholder")}</option>
                {regions.map((r) => (
                  <option key={r} value={r}>
                    {r}
                  </option>
                ))}
              </select>
            </div>
            <div className="px-0">
              <label className="block text-sm text-gray-700 mb-1 ml-0">
                {t("deals.modal.noteLabel")}
              </label>
              <textarea
                className="w-full border border-gray-300 rounded-lg px-4 py-2 text-gray-700 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-teal-200"
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder={t("deals.modal.notePlaceholder")}
                rows={3}
              />
            </div>
            <div className="px-0">
              <label className="block text-sm text-gray-700 mb-3 ml-0">
                {t("deals.modal.servicesLabel")}
              </label>
              <div className="space-y-2">
                {services.map((service) => (
                  <div key={service} className="flex items-center">
                    <input
                      type="checkbox"
                      id={service}
                      checked={selectedServices.includes(service)}
                      onChange={() => handleServiceToggle(service)}
                      className="h-4 w-4 text-teal-600 focus:ring-teal-500 border-gray-300 rounded"
                    />
                    <label
                      htmlFor={service}
                      className="ml-3 text-sm text-gray-700 cursor-pointer"
                    >
                      {service}
                    </label>
                  </div>
                ))}
              </div>
            </div>
            {error && (
              <div className="text-red-600 text-sm font-medium mt-2 px-0">
                {error}
              </div>
            )}
            <div className="flex justify-end gap-3 pt-2 px-0">
              <button
                type="button"
                className="px-6 py-2 rounded-lg border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 font-medium"
                onClick={onClose}
                disabled={
                  createDealMutation.isPending || updateDealMutation.isPending
                }
              >
                {t("common.cancel")}
              </button>
              <button
                type="submit"
                className="px-6 py-2 rounded-lg bg-teal-600 text-white hover:bg-teal-700 font-medium flex items-center gap-2 disabled:opacity-50"
                disabled={
                  createDealMutation.isPending || updateDealMutation.isPending
                }
              >
                {createDealMutation.isPending ||
                updateDealMutation.isPending ? (
                  <>
                    <i className="fa-solid fa-spinner fa-spin"></i>
                    {isEditing ? t("deals.modal.updating") : t("deals.modal.creating")}
                  </>
                ) : (
                  <>
                    <i className="fa-solid fa-floppy-disk"></i>
                    {isEditing ? t("deals.modal.submitUpdate") : t("deals.modal.submitCreate")}
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </CustomModal>
  );
};

export default NewDealModal;
